import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

export const TransitionDialog = ({ woId, wo, transition, onOpenChange, onDone }) => {
  const { t } = useT();
  const [motivo, setMotivo] = useState("");
  const [medio, setMedio] = useState("");
  const [force, setForce] = useState(false);
  const [billingBrand, setBillingBrand] = useState("");
  const [saving, setSaving] = useState(false);

  const needsMedio = transition?.destino === "aceptada";
  const isClosing = transition?.destino === "cerrada";
  const open = !!transition;

  const { data: qboSettings } = useQuery({
    queryKey: ["qbo-settings"],
    queryFn: async () => (await api.get("/qbo/settings")).data,
    enabled: isClosing,
  });
  const needsBrand = isClosing && qboSettings?.facturacion_qbo?.regla === "manual" && !wo?.billing_brand;

  const submit = async () => {
    setSaving(true);
    try {
      if (needsBrand) {
        if (!billingBrand) { toast.error(t("wo.detail.qbo.choose_brand")); setSaving(false); return; }
        await api.patch(`/qbo/work-orders/${woId}/billing-brand`, { billing_brand: billingBrand });
      }
      await api.post(`/work-orders/${woId}/transition`, {
        destino: transition.destino,
        motivo: motivo || undefined,
        medio: medio || undefined,
        force,
      });
      toast.success(t("wo.transition.done"));
      onDone?.();
      onOpenChange(false);
      setMotivo("");
      setMedio("");
      setForce(false);
      setBillingBrand("");
    } catch (e) {
      toast.error(apiError(e, t("app.error")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="wo-transition-dialog" className="max-w-[480px] rounded-surface">
        <DialogHeader>
          <DialogTitle className="text-[16px]">{transition?.label}</DialogTitle>
          <DialogDescription className="text-[13px]">
            {t(`woStatus.${transition?.destino}`)}
          </DialogDescription>
        </DialogHeader>

        {needsMedio && (
          <div>
            <Label className="text-[12.5px]">{t("wo.transition.medio")}</Label>
            <Input
              data-testid="wo-transition-medio"
              value={medio}
              onChange={(e) => setMedio(e.target.value)}
              placeholder={t("wo.transition.medio_ph")}
              className="mt-1 h-9 text-[13px]"
            />
          </div>
        )}

        <div>
          <Label className="text-[12.5px]">
            {transition?.motivo ? `${t("wo.transition.motivo")} *` : t("wo.transition.motivo_optional")}
          </Label>
          <Textarea
            data-testid="wo-transition-motivo"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            placeholder={transition?.motivo ? t("wo.transition.motivo_ph") : t("wo.transition.motivo_optional_ph")}
            className="mt-1 text-[13px]"
          />
        </div>

        {needsBrand && (
          <div>
            <Label className="text-[12.5px]">{t("wo.detail.qbo.choose_brand")} *</Label>
            <Select value={billingBrand} onValueChange={setBillingBrand}>
              <SelectTrigger data-testid="wo-transition-billing-brand" className="mt-1 h-9 text-[13px]">
                <SelectValue placeholder={t("wo.detail.qbo.choose_brand")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bb">B&amp;B</SelectItem>
                <SelectItem value="betos">Betos</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}

        {transition?.forceable && (
          <label className="flex items-start gap-2 text-[12.5px] text-ink-secondary">
            <Checkbox data-testid="wo-transition-force" checked={force} onCheckedChange={setForce} />
            {t("wo.transition.force")}
          </label>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="h-9 text-[13px]">
            {t("app.cancel")}
          </Button>
          <Button
            data-testid="wo-transition-confirm"
            disabled={saving || (needsBrand && !billingBrand)}
            onClick={submit}
            className="h-9 text-[13px]"
          >
            {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
            {t("wo.transition.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
